import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Select,
  MenuItem,
  TextField,
  Chip,
  TablePagination,
  CircularProgress,
  Skeleton,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Tooltip
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiUrl, parseApiError } from "./utils/api";
import { addRecentJob } from "./utils/jobCookies";

const DEFAULT_MATCH_LIMIT = 50;
const MAX_DETAIL_CACHE_ENTRIES = 10;
// Sentinel tab id for the merged "All Matches" view -- can't collide with a
// real query_pair_id (those always come back from the backend as
// "<pdbid>:<chainA>-<chainB>#<n>").
const ALL_MATCHES_TAB_ID = "__all_matches__";

const uniqueChains = chains => Array.from(
  new Set((Array.isArray(chains) ? chains : [chains]).filter(Boolean))
);

const getAllTargetChains = row => uniqueChains(
  row?.qgraph_chains || row?.All_Target_Chains || row?.Target_Chains || row?.Target_Chain
);

const getSelectedTargetChain = (row, selectedTargetChain) => {
  if (!selectedTargetChain) return "";

  const allTargetChains = getAllTargetChains(row);
  return allTargetChains.includes(selectedTargetChain) ? selectedTargetChain : "";
};

const getChainOptionForTarget = (row, selectedTargetChain) => {
  const resolvedTargetChain = getSelectedTargetChain(row, selectedTargetChain);
  if (!resolvedTargetChain) return null;

  const lookupOption = row?.chain_lookup?.[resolvedTargetChain];
  if (lookupOption) return lookupOption;

  const options = Array.isArray(row?.chain_options) ? row.chain_options : [];
  return options.find(option =>
    option?.qgraph_chain === resolvedTargetChain || option?.value === resolvedTargetChain
  ) || null;
};

const getTargetChains = (row, selectedTargetChain) => {
  const resolvedTargetChain = getSelectedTargetChain(row, selectedTargetChain);
  return resolvedTargetChain ? [resolvedTargetChain] : getAllTargetChains(row);
};

const getLigandChainsFromOption = chainOption => {
  const mappingAlternativeChains = uniqueChains(
    (Array.isArray(chainOption?.mappings) ? chainOption.mappings : []).flatMap(mapping =>
      mapping?.alternative_cgraph_chains ||
      mapping?.alternative_lig_chains ||
      []
    )
  );
  if (mappingAlternativeChains.length > 0) {
    return mappingAlternativeChains;
  }

  return uniqueChains(
    chainOption?.alternative_cgraph_chains ||
    chainOption?.mapped_cgraph_chains ||
    []
  );
};

const getLigandChains = (row, selectedTargetChain) => {
  const resolvedTargetChain = getSelectedTargetChain(row, selectedTargetChain);
  const chainOption = getChainOptionForTarget(row, selectedTargetChain);
  const scopedLigandChains = getLigandChainsFromOption(chainOption);

  if (scopedLigandChains.length > 0) {
    return scopedLigandChains;
  }

  if (!resolvedTargetChain) {
    const allChainOptions = Array.isArray(row?.chain_options) ? row.chain_options : [];
    const aggregatedLigandChains = uniqueChains(
      allChainOptions.flatMap(option => getLigandChainsFromOption(option))
    );

    if (aggregatedLigandChains.length > 0) {
      return aggregatedLigandChains;
    }
  }

  return uniqueChains(
    row?.alternative_cgraph_chains ||
    row?.mapped_cgraph_chains ||
    row?.predicted_ligand_chains ||
    row?.cgraph_chains ||
    row?.Ligand_Chains
  );
};

const buildVisualizationRow = (detail, summaryRow, selectedTargetChain) => {
  const legacy = detail?.legacy || {};
  const targetChains = uniqueChains(detail?.qgraph_chains || legacy.Target_Chains || []);
  const resolvedTargetChain = selectedTargetChain || detail?.default_qgraph_chain || legacy.Target_Chain || targetChains[0];
  const ligandChains = getLigandChains(detail, resolvedTargetChain);

  return {
    ...summaryRow,
    ...detail,
    PDBID_Targ: legacy.PDBID_Targ || detail?.qgraph_pdbid,
    PDBID_Lig: legacy.PDBID_Lig || detail?.cgraph_pdbid,
    Targ_Size: legacy.Targ_Size ?? detail?.qgraph_size,
    Lig_Size: legacy.Lig_Size ?? detail?.cgraph_size,
    Target_Edges: legacy.Target_Edges ?? detail?.qgraph_edge_count,
    Num_Mappings: legacy.Num_Mappings ?? detail?.Matched_Patterns_Total ?? detail?.matched_patterns_total,
    Target_Chain: resolvedTargetChain,
    Target_Chains: getTargetChains(detail, resolvedTargetChain),
    All_Target_Chains: legacy.All_Target_Chains || targetChains,
    Ligand_Chains: ligandChains,
    predicted_ligand_chains: ligandChains,
    Chain_Mappings: legacy.Chain_Mappings || {},
    target_graph: detail?.qgraph,
    ligand_graph: detail?.cgraph,
    selected_target_chain: resolvedTargetChain,
  };
};

export default function Result() {
  const location = useLocation();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const {
    results: stateResults,
    pdbName: statePdbName,
    jobStatus: stateJobStatus,
    matchPageData: stateMatchPageData,
    page: statePage,
    rowsPerPage: stateRowsPerPage,
    targetChainFilter: stateTargetChainFilter,
    targetSizeFilter: stateTargetSizeFilter,
    graphPatternSizeFilter: stateGraphPatternSizeFilter,
    numMappingFilter: stateNumMappingFilter,
    pickScoreFilter: statePickScoreFilter,
    sortField: stateSortField,
    sortOrder: stateSortOrder,
    selectedQueryGraphs: stateSelectedQueryGraphs,
  } = location.state || {};

  const [jobStatus, setJobStatus] = useState(stateJobStatus || null);
  const [jobResults, setJobResults] = useState(Array.isArray(stateResults) ? stateResults : null);
  const [jobError, setJobError] = useState("");
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState("");
  const [matchPageData, setMatchPageData] = useState(stateMatchPageData || null);

  const [targetChainFilter, setTargetChainFilter] = useState(stateTargetChainFilter || "");
  const [targetSizeFilter, setTargetSizeFilter] = useState(stateTargetSizeFilter || "");
  const [graphPatternSizeFilter, setGraphPatternSizeFilter] = useState(stateGraphPatternSizeFilter || "");
  const [numMappingFilter, setNumMappingFilter] = useState(stateNumMappingFilter || "");
  const [pickScoreFilter, setPickScoreFilter] = useState(statePickScoreFilter || "");
  const [selectedQueryGraphs, setSelectedQueryGraphs] = useState(
    Array.isArray(stateSelectedQueryGraphs) ? stateSelectedQueryGraphs : []
  );
  // Which query-graph tab is active, and each tab's own remembered page
  // number, so switching tabs doesn't lose your place or force a refetch.
  const [activeQueryGraphTab, setActiveQueryGraphTab] = useState("");
  const [tabPages, setTabPages] = useState({});

  const [sortField, setSortField] = useState(stateSortField || "");
  const [sortOrder, setSortOrder] = useState(stateSortOrder || "asc");

  const [page, setPage] = useState(Number.isInteger(statePage) ? statePage : 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number.isInteger(stateRowsPerPage) ? stateRowsPerPage : 10
  );
  const [downloading, setDownloading] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [detailLoadingMatchId, setDetailLoadingMatchId] = useState("");

  const terminalStateReached = useRef(false);
  const lastRequestedMatchesKey = useRef("");
  const matchDetailCache = useRef(new Map());
  // Keyed by scope+page+limit; lets switching between query-graph tabs reuse
  // an already-fetched page instead of refetching it.
  const matchPageCache = useRef(new Map());

  useEffect(() => {
    if (!stateResults && !jobId) {
      navigate("/");
    }
  }, [stateResults, jobId, navigate]);

  useEffect(() => {
    terminalStateReached.current = Boolean(
      stateJobStatus?.status === "completed" &&
      stateJobStatus?.result_ready &&
      (Array.isArray(stateResults) || stateMatchPageData)
    );
    lastRequestedMatchesKey.current =
      stateMatchPageData && Number.isInteger(statePage) && Number.isInteger(stateRowsPerPage)
        ? `${jobId}:${statePage + 1}:${stateRowsPerPage}`
        : "";
    setJobError("");
    setMatchesError("");
    setJobStatus(stateJobStatus || null);
    setMatchPageData(stateMatchPageData || null);
    setDetailLoadingMatchId("");
    setTargetChainFilter(stateTargetChainFilter || "");
    setTargetSizeFilter(stateTargetSizeFilter || "");
    setGraphPatternSizeFilter(stateGraphPatternSizeFilter || "");
    setNumMappingFilter(stateNumMappingFilter || "");
    setPickScoreFilter(statePickScoreFilter || "");
    setSelectedQueryGraphs(Array.isArray(stateSelectedQueryGraphs) ? stateSelectedQueryGraphs : []);
    setActiveQueryGraphTab("");
    setTabPages({});
    setSortField(stateSortField || "");
    setSortOrder(stateSortOrder || "asc");
    setPage(Number.isInteger(statePage) ? statePage : 0);
    setRowsPerPage(Number.isInteger(stateRowsPerPage) ? stateRowsPerPage : 10);
    matchDetailCache.current = new Map();
    matchPageCache.current = new Map();
    setJobResults(Array.isArray(stateResults) ? stateResults : null);
  }, [
    jobId,
    stateJobStatus,
    stateMatchPageData,
    stateGraphPatternSizeFilter,
    stateNumMappingFilter,
    statePage,
    statePickScoreFilter,
    stateResults,
    stateRowsPerPage,
    stateSelectedQueryGraphs,
    stateSortField,
    stateSortOrder,
    stateTargetChainFilter,
    stateTargetSizeFilter,
  ]);

  useEffect(() => {
    if (!jobId) return undefined;
    if (terminalStateReached.current) return undefined;

    let active = true;
    let timer = null;
    let requestInFlight = false;

    const pollJob = async () => {
      if (!active || requestInFlight || terminalStateReached.current) return;

      requestInFlight = true;
      setIsPolling(true);

      try {
        const response = await fetch(apiUrl(`/jobs/${jobId}`), {
          credentials: "include",
        });

        if (!response.ok) {
          const message = await parseApiError(response, "Unable to load prediction job");
          throw new Error(message);
        }

        const data = await response.json();
        if (!active) return;

        setJobStatus(data);

        if (data.status === "completed" && data.result_ready) {
          terminalStateReached.current = true;
          setIsPolling(false);
          if (Array.isArray(data.results?.raw_matches)) {
            setJobResults(data.results.raw_matches);
          }
          addRecentJob({
            job_id: data.job_id,
            pdb_name: data.pdb_name,
            result_url: data.result_url,
            created_at: data.created_at,
          });
          return;
        }

        if (data.status === "failed") {
          terminalStateReached.current = true;
          setIsPolling(false);
          setJobError(data.error || "Prediction failed");
          return;
        }

        timer = window.setTimeout(pollJob, 3000);
      } catch (error) {
        if (!active) return;
        setIsPolling(false);
        setJobError(
          error.message === "Failed to fetch"
            ? "Unable to connect to the prediction backend. Make sure the Flask server is running and the result link is opened on the same machine or configured backend URL."
            : error.message || "Unable to load prediction job"
        );
      } finally {
        requestInFlight = false;
      }
    };

    pollJob();

    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId || !jobStatus || jobStatus.status !== "completed" || !jobStatus.result_ready) {
      return undefined;
    }

    if (Array.isArray(stateResults) && stateResults.length) {
      return undefined;
    }

    if (Array.isArray(jobResults) && jobResults.length) {
      return undefined;
    }

    // A job with query graphs but none selected means the user deliberately
    // deselected everything -- skip fetching entirely rather than falling
    // back to "no filter" (which would fetch every match) or leaving stale
    // data on screen; the render shows an explicit "select at least one"
    // state for this case instead. Derived from jobStatus directly (rather
    // than the queryGraphIdsKey declared further down) since dependency
    // arrays are evaluated at this call site, before later declarations
    // in the component exist.
    const hasQueryGraphs = (jobStatus?.summary?.query_graphs?.length ?? 0) > 0;
    if (hasQueryGraphs && selectedQueryGraphs.length === 0) {
      return undefined;
    }

    // When more than one query graph is selected, the visible table is
    // scoped to whichever tab is active -- one query graph at a time, its
    // own page -- unless the "All Matches" tab is active, which sends every
    // selected query graph as its own `query=` param so the backend merges
    // them into one filtered+paginated list instead of just one interface.
    const isTabbed = selectedQueryGraphs.length > 1 && Boolean(activeQueryGraphTab);
    const effectiveQueryFilter = isTabbed
      ? (activeQueryGraphTab === ALL_MATCHES_TAB_ID ? selectedQueryGraphs : [activeQueryGraphTab])
      : selectedQueryGraphs;
    const effectivePage = isTabbed ? (tabPages[activeQueryGraphTab] ?? 0) : page;

    let active = true;
    const requestedPage = effectivePage + 1;
    // Sorted so re-selecting the same set in a different order doesn't look
    // like a new request and trigger an unnecessary refetch.
    const queryGraphFilterKey = [...effectiveQueryFilter].sort().join(",");
    // The filter/sort controls are applied server-side (a job can have
    // thousands of matches across hundreds of pages, so filtering only the
    // page that happens to be loaded would silently miss almost everything
    // that actually matches) -- folded into the cache/de-dupe key so
    // changing any of them triggers a real refetch instead of reusing a
    // stale page fetched under different filters.
    const filterKey = [
      targetChainFilter,
      targetSizeFilter,
      graphPatternSizeFilter,
      numMappingFilter,
      pickScoreFilter,
      sortField,
      sortOrder
    ].join("|");
    const requestKey = `${jobId}:${requestedPage}:${rowsPerPage}:${queryGraphFilterKey}:${filterKey}`;

    const cachedPage = matchPageCache.current.get(requestKey);
    if (cachedPage) {
      setMatchPageData(cachedPage);
      return undefined;
    }

    if (lastRequestedMatchesKey.current === requestKey) {
      return undefined;
    }

    lastRequestedMatchesKey.current = requestKey;
    setMatchesLoading(true);
    setMatchesError("");

    const loadMatchesPage = async () => {
      try {
        const matchesUrl = jobStatus.matches_url || `/jobs/${jobId}/matches`;
        const separator = matchesUrl.includes("?") ? "&" : "?";
        // Omitting the query filter entirely (empty selection, or a job
        // whose summary predates the query-graph catalog) preserves today's
        // "all matches" behavior -- the backend only scopes results when at
        // least one `query` param is present.
        const queryGraphParams = effectiveQueryFilter
          .map(queryPairId => `query=${encodeURIComponent(queryPairId)}`)
          .join("&");
        const queryGraphSuffix = queryGraphParams ? `&${queryGraphParams}` : "";

        const filterParams = [
          targetChainFilter && `target_chain=${encodeURIComponent(targetChainFilter)}`,
          targetSizeFilter !== "" && `qgraph_size=${encodeURIComponent(targetSizeFilter)}`,
          graphPatternSizeFilter !== "" && `cgraph_size=${encodeURIComponent(graphPatternSizeFilter)}`,
          numMappingFilter !== "" && `matched_patterns_total=${encodeURIComponent(numMappingFilter)}`,
          pickScoreFilter !== "" && `min_pick_score=${encodeURIComponent(pickScoreFilter)}`,
          sortField && `sort_field=${encodeURIComponent(sortField)}`,
          sortField && `sort_order=${encodeURIComponent(sortOrder)}`
        ].filter(Boolean).join("&");
        const filterSuffix = filterParams ? `&${filterParams}` : "";

        const response = await fetch(
          apiUrl(`${matchesUrl}${separator}page=${requestedPage}&limit=${rowsPerPage}${queryGraphSuffix}${filterSuffix}`),
          { credentials: "include" }
        );

        if (!response.ok) {
          const message = await parseApiError(response, "Unable to load match summaries");
          throw new Error(message);
        }

        const data = await response.json();
        if (!active) return;

        matchPageCache.current.set(requestKey, data);
        setMatchPageData(data);
      } catch (error) {
        if (!active) return;
        lastRequestedMatchesKey.current = "";
        setMatchesError(error.message || "Unable to load match summaries");
      } finally {
        if (active) {
          setMatchesLoading(false);
        }
      }
    };

    loadMatchesPage();

    return () => {
      active = false;
    };
  }, [
    activeQueryGraphTab,
    graphPatternSizeFilter,
    jobId,
    jobResults,
    jobStatus,
    numMappingFilter,
    page,
    pickScoreFilter,
    rowsPerPage,
    selectedQueryGraphs,
    sortField,
    sortOrder,
    stateResults,
    tabPages,
    targetChainFilter,
    targetSizeFilter
  ]);

  useEffect(() => {
    if (activeQueryGraphTab) {
      setTabPages(prev => (prev[activeQueryGraphTab] ? { ...prev, [activeQueryGraphTab]: 0 } : prev));
    } else {
      setPage(0);
    }
  }, [targetChainFilter, targetSizeFilter, graphPatternSizeFilter, numMappingFilter, pickScoreFilter, selectedQueryGraphs, sortField, sortOrder]);

  const results = stateResults || jobResults || matchPageData?.items || [];
  const deferredResults = useDeferredValue(results);
  const deferredTargetChainFilter = useDeferredValue(targetChainFilter);
  const deferredTargetSizeFilter = useDeferredValue(targetSizeFilter);
  const deferredGraphPatternSizeFilter = useDeferredValue(graphPatternSizeFilter);
  const deferredNumMappingFilter = useDeferredValue(numMappingFilter);
  const deferredPickScoreFilter = useDeferredValue(pickScoreFilter);
  const summary = jobStatus?.summary || null;
  const queryGraphs = useMemo(
    () => (Array.isArray(summary?.query_graphs) ? summary.query_graphs : []),
    [summary]
  );
  const queryGraphIdsKey = useMemo(
    () => queryGraphs.map(queryGraph => queryGraph.query_pair_id).join("|"),
    [queryGraphs]
  );
  const chainsWithoutInterface = useMemo(
    () => (Array.isArray(summary?.chains_without_interface) ? summary.chains_without_interface : []),
    [summary]
  );
  // A job that has query graphs but none currently selected means the user
  // deliberately unchecked everything -- show that explicitly instead of
  // silently falling back to "no filter" (which would show every match) or
  // an empty table that reads as "zero matches found".
  const showEmptySelectionState = queryGraphs.length > 0 && selectedQueryGraphs.length === 0;

  // A job with more than one detected query-graph interface (e.g. a
  // multi-chain PDB) defaults to "all selected" so the common single-interface
  // case behaves exactly as before with zero extra clicks. This only fires
  // once per distinct set of query graphs, so it never overwrites a
  // restored selection or a deliberate user deselection.
  useEffect(() => {
    if (!queryGraphIdsKey) return;

    setSelectedQueryGraphs(prev => (prev.length > 0 ? prev : queryGraphIdsKey.split("|")));
  }, [queryGraphIdsKey]);

  // Only worth tabbing when there's more than one query graph actually
  // selected -- a single selection (the common 2-chain case) renders the
  // flat table exactly as before.
  const tabbedQueryGraphs = useMemo(
    () => (selectedQueryGraphs.length > 1
      ? queryGraphs.filter(queryGraph => selectedQueryGraphs.includes(queryGraph.query_pair_id))
      : []),
    [queryGraphs, selectedQueryGraphs]
  );
  const isTabbed = tabbedQueryGraphs.length > 0;
  const allMatchesTotal = useMemo(
    () => tabbedQueryGraphs.reduce((sum, queryGraph) => sum + (queryGraph.match_count || 0), 0),
    [tabbedQueryGraphs]
  );

  // Keep the active tab valid: pick the first tab on entering tabbed mode,
  // and fall back to the first remaining tab if the active one gets
  // deselected. Clears back out when leaving tabbed mode entirely.
  useEffect(() => {
    if (!isTabbed) {
      if (activeQueryGraphTab) setActiveQueryGraphTab("");
      return;
    }

    const isValidTab = activeQueryGraphTab === ALL_MATCHES_TAB_ID ||
      tabbedQueryGraphs.some(queryGraph => queryGraph.query_pair_id === activeQueryGraphTab);
    if (!isValidTab) {
      setActiveQueryGraphTab(tabbedQueryGraphs[0].query_pair_id);
    }
  }, [activeQueryGraphTab, isTabbed, tabbedQueryGraphs]);

  const currentPage = isTabbed ? (tabPages[activeQueryGraphTab] ?? 0) : page;
  const setCurrentPage = newPage => {
    if (isTabbed) {
      setTabPages(prev => ({ ...prev, [activeQueryGraphTab]: newPage }));
    } else {
      setPage(newPage);
    }
  };

  const pdbName = statePdbName || jobStatus?.pdb_name || "prediction";
  const currentJobId = jobId || jobStatus?.job_id;
  const jobLink = currentJobId ? `${window.location.origin}/result/${currentJobId}` : "";
  const resultRoute = currentJobId ? `/result/${currentJobId}` : "/result";
  const usingServerPagination = Boolean(matchPageData && !stateResults && !jobResults);
  // When server pagination is driving the view, matchPageData.total_matches
  // is the authoritative count for whatever filters (including the selected
  // query graphs) are currently applied to that request. summary.match_count
  // is the job-wide total and would misrepresent an active filter as if it
  // covered every match, letting pagination advance into empty pages.
  const totalMatchCount = usingServerPagination
    ? (matchPageData?.total_matches ?? results.length)
    : (summary?.match_count || matchPageData?.total_matches || results.length);
  const totalPages = matchPageData?.total_pages || (totalMatchCount ? Math.ceil(totalMatchCount / rowsPerPage) : 0);

  const allTargetChains = useMemo(
    () => Array.from(new Set(deferredResults.flatMap(row => getTargetChains(row)))).filter(Boolean),
    [deferredResults]
  );

  const safeFileName = value =>
    String(value || "prediction")
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-z0-9_-]+/gi, "_")
      .replace(/^_+|_+$/g, "");

  const exportRows = rows => rows.map(row => ({
    ...row,
    Target_Chains_Display: getTargetChains(row, targetChainFilter),
    Ligand_Chains_Display: getLigandChains(row, targetChainFilter)
  }));

  const downloadBlob = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const downloadServerResult = type => {
    const link = document.createElement("a");
    link.href = apiUrl(`/jobs/${currentJobId}/download/${type}`);
    link.download = "";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Mirrors the query-graph and filter/sort params the live table sends to
  // `/jobs/<id>/matches` (see the match-summaries fetch effect above) so a
  // filtered download matches what's actually filtered on screen.
  const buildMatchesFilterQuery = () => {
    const isTabbed = selectedQueryGraphs.length > 1 && Boolean(activeQueryGraphTab);
    const effectiveQueryFilter = isTabbed
      ? (activeQueryGraphTab === ALL_MATCHES_TAB_ID ? selectedQueryGraphs : [activeQueryGraphTab])
      : selectedQueryGraphs;
    const params = [
      ...effectiveQueryFilter.map(queryPairId => `query=${encodeURIComponent(queryPairId)}`),
      targetChainFilter && `target_chain=${encodeURIComponent(targetChainFilter)}`,
      targetSizeFilter !== "" && `qgraph_size=${encodeURIComponent(targetSizeFilter)}`,
      graphPatternSizeFilter !== "" && `cgraph_size=${encodeURIComponent(graphPatternSizeFilter)}`,
      numMappingFilter !== "" && `matched_patterns_total=${encodeURIComponent(numMappingFilter)}`,
      pickScoreFilter !== "" && `min_pick_score=${encodeURIComponent(pickScoreFilter)}`,
      sortField && `sort_field=${encodeURIComponent(sortField)}`,
      sortField && `sort_order=${encodeURIComponent(sortOrder)}`
    ].filter(Boolean);

    return params.join("&");
  };

  // With any filter/sort active, the rows currently rendered are only
  // whatever page happens to be loaded -- a filtered download needs every
  // matching row across every page, not just that one page's slice.
  const fetchAllFilteredMatches = async () => {
    const matchesUrl = jobStatus?.matches_url || `/jobs/${currentJobId}/matches`;
    const separator = matchesUrl.includes("?") ? "&" : "?";
    const filterQuery = buildMatchesFilterQuery();
    const filterSuffix = filterQuery ? `&${filterQuery}` : "";
    const limit = 100;

    const fetchPage = async pageNum => {
      const response = await fetch(
        apiUrl(`${matchesUrl}${separator}page=${pageNum}&limit=${limit}${filterSuffix}`),
        { credentials: "include" }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, "Unable to load matches for download"));
      }
      return response.json();
    };

    const firstPage = await fetchPage(1);
    const allItems = [...(firstPage.items || [])];
    const totalPages = firstPage.total_pages || 1;

    for (let pageNum = 2; pageNum <= totalPages; pageNum += 1) {
      const data = await fetchPage(pageNum);
      allItems.push(...(data.items || []));
    }

    return allItems;
  };

  const csvValue = value => {
    const stringValue = typeof value === "object" && value !== null
      ? JSON.stringify(value)
      : String(value ?? "");

    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const downloadJsonResults = rows => {
    const content = JSON.stringify({ raw_matches: exportRows(rows) }, null, 2);
    downloadBlob(
      content,
      "application/json",
      `${safeFileName(pdbName)}_results.json`
    );
  };

  const downloadCsvResults = rows => {
    const data = exportRows(rows);
    const columns = Array.from(new Set(data.flatMap(row => Object.keys(row))));
    const csv = [
      columns.join(","),
      ...data.map(row => columns.map(column => csvValue(row[column])).join(","))
    ].join("\n");

    downloadBlob(
      csv,
      "text/csv;charset=utf-8",
      `${safeFileName(pdbName)}_results.csv`
    );
  };

  const rollingDownloadIcon = active => (
    active ? (
      <AutorenewIcon
        sx={{
          "@keyframes downloadRoll": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" }
          },
          animation: "downloadRoll 0.75s linear infinite"
        }}
      />
    ) : (
      <FileDownloadIcon />
    )
  );

  const handleDownload = async (type, rows) => {
    setDownloading(type);

    const hasFieldFilters = Boolean(
      targetChainFilter || targetSizeFilter || graphPatternSizeFilter || numMappingFilter || pickScoreFilter || sortField
    );
    // Being on a specific query-graph tab, or having deselected some query
    // graphs in the "Graph Query Interfaces" picker, scopes the table just
    // as much as the filter fields do -- the download should match what's
    // actually on screen, not silently widen back out to every match in
    // the job.
    const isNarrowedByTab = isTabbed && activeQueryGraphTab !== ALL_MATCHES_TAB_ID;
    const isNarrowedByQueryGraphSelection =
      queryGraphs.length > 0 && selectedQueryGraphs.length > 0 && selectedQueryGraphs.length < queryGraphs.length;
    const hasActiveFilters = hasFieldFilters || isNarrowedByTab || isNarrowedByQueryGraphSelection;
    const canUseServerFile = currentJobId && jobStatus?.status === "completed" && !hasActiveFilters;

    try {
      if (canUseServerFile) {
        downloadServerResult(type);
        return;
      }

      // Whatever's currently rendered might be only one server-paginated
      // page, or (for small jobs loaded fully client-side) the query-graph-
      // unscoped complete array -- re-fetch the actual filtered/scoped set
      // from the server so the export always matches every filter and tab
      // currently applied, not just whatever happens to already be on screen.
      const exportSource = currentJobId
        ? await fetchAllFilteredMatches()
        : rows;

      if (type === "json") {
        downloadJsonResults(exportSource);
      } else {
        downloadCsvResults(exportSource);
      }
    } catch (error) {
      setMatchesError(error.message || "Unable to prepare download");
    } finally {
      setDownloading("");
    }
  };

  const navigateToVisualization = (path, row, detail, selectedTargetChain) => {
    navigate(path, {
      state: {
        rowData: buildVisualizationRow(detail, row, selectedTargetChain),
        returnTo: resultRoute,
        returnState: {
          matchPageData,
          page,
          rowsPerPage,
          targetChainFilter,
          targetSizeFilter,
          graphPatternSizeFilter,
          numMappingFilter,
          pickScoreFilter,
          selectedQueryGraphs,
          sortField,
          sortOrder,
          pdbName,
          jobId: currentJobId,
          results: jobResults,
          jobStatus,
        },
      }
    });
  };

  const handleVisualize = async row => {
    if (!currentJobId) return;

    const path = "/graph-explorer";
    const matchId = row.match_id || row.Match_ID;
    if (!matchId) return;

    const selectedTargetChain = targetChainFilter || row.default_qgraph_chain || getTargetChains(row)[0];
    const cachedDetail = matchDetailCache.current.get(matchId);

    if (cachedDetail) {
      navigateToVisualization(path, row, cachedDetail, selectedTargetChain);
      return;
    }

    setDetailLoadingMatchId(matchId);
    setMatchesError("");

    try {
      const response = await fetch(
        apiUrl(`/jobs/${currentJobId}/matches/${encodeURIComponent(matchId)}`),
        { credentials: "include" }
      );

      if (!response.ok) {
        const message = await parseApiError(response, "Unable to load match detail");
        throw new Error(message);
      }

      const data = await response.json();
      const detail = data.detail;
      matchDetailCache.current.set(matchId, detail);
      if (matchDetailCache.current.size > MAX_DETAIL_CACHE_ENTRIES) {
        const oldestKey = matchDetailCache.current.keys().next().value;
        if (oldestKey) {
          matchDetailCache.current.delete(oldestKey);
        }
      }

      navigateToVisualization(path, row, detail, selectedTargetChain);
    } catch (error) {
      setMatchesError(error.message || "Unable to load match detail");
    } finally {
      setDetailLoadingMatchId("");
    }
  };

  const filteredResults = useMemo(() => deferredResults.filter(row =>
    (
      deferredTargetChainFilter === "" ||
      getTargetChains(row).includes(deferredTargetChainFilter)
    ) &&
    (
      deferredTargetSizeFilter === "" ||
      Number(row.qgraph_size ?? row.Targ_Size) === Number(deferredTargetSizeFilter)
    ) &&
    (
      deferredGraphPatternSizeFilter === "" ||
      Number(row.cgraph_size ?? row.CGraph_Size ?? row.Lig_Size ?? 0) === Number(deferredGraphPatternSizeFilter)
    ) &&
    (
      deferredNumMappingFilter === "" ||
      Number(row.matched_patterns_total ?? row.Matched_Patterns_Total ?? 0) === Number(deferredNumMappingFilter)
    ) &&
    (
      deferredPickScoreFilter === "" ||
      Number(row.pick_score ?? 0) >= Number(deferredPickScoreFilter)
    )
  ), [
    deferredNumMappingFilter,
    deferredPickScoreFilter,
    deferredResults,
    deferredGraphPatternSizeFilter,
    deferredTargetChainFilter,
    deferredTargetSizeFilter
  ]);

  const sortedResults = useMemo(() => [...filteredResults].sort((a, b) => {
    if (!sortField) return 0;

    const normalizeValue = row => {
      if (sortField === "qgraph_size") return Number(row.qgraph_size ?? row.Targ_Size ?? 0);
      if (sortField === "cgraph_size") return Number(row.cgraph_size ?? row.CGraph_Size ?? row.Lig_Size ?? 0);
      if (sortField === "matched_patterns_total") return Number(row.matched_patterns_total ?? row.Matched_Patterns_Total ?? 0);
      if (sortField === "pick_score") return Number(row.pick_score ?? 0);
      return Number(row[sortField] ?? 0);
    };

    const valA = normalizeValue(a);
    const valB = normalizeValue(b);
    return sortOrder === "asc" ? valA - valB : valB - valA;
  }), [filteredResults, sortField, sortOrder]);

  const paginatedResults = useMemo(() => usingServerPagination
    ? sortedResults
    : sortedResults.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
      ), [currentPage, rowsPerPage, sortedResults, usingServerPagination]);

  // `results` is empty both while a job is still loading AND when a
  // completed job's filters simply matched zero rows on the current
  // server-paginated page -- `matchPageData` existing means the fetch
  // already succeeded, so that second case should fall through to the
  // normal results page (with an empty table) instead of the "Prediction
  // Status" screen meant for a job that hasn't produced results yet.
  if (!results.length && !matchPageData) {
    if (jobId) {
      return (
        <Box component="section" className="result" sx={{ padding: { xs: "2rem 4%", md: "8rem 10%" }, height: "92vh", textAlign: "center", py: 4 }}>
          <Typography variant="h4" gutterBottom>Prediction Status</Typography>
          <Typography sx={{ mb: 2, wordBreak: "break-all" }}>
            Job ID: <b>{jobId}</b>
          </Typography>

          {jobError ? (
            <Typography sx={{ color: "red", fontWeight: 600 }}>{jobError}</Typography>
          ) : matchesError ? (
            <Typography sx={{ color: "red", fontWeight: 600 }}>{matchesError}</Typography>
          ) : jobStatus?.status === "completed" && jobStatus?.result_ready ? (
            matchesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                <CircularProgress size={22} />
                <Typography>Prediction completed. Loading match summaries...</Typography>
              </Box>
            ) : (
              <Typography sx={{ color: "green", fontWeight: 600 }}>
                Prediction completed. Results are ready.
              </Typography>
            )
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
              {isPolling && <CircularProgress size={22} />}
              <Typography>
                {jobStatus?.status
                  ? `Prediction is ${jobStatus.status}.${isPolling ? " This page will update automatically." : ""}`
                  : "Loading prediction status..."}
              </Typography>
            </Box>
          )}

          <Typography sx={{ mt: 3, wordBreak: "break-all" }}>
            Dedicated result link: <b>{jobLink}</b>
          </Typography>
          <Button sx={{ mt: 3 }} variant="outlined" onClick={() => navigate("/")}>
            Back Home
          </Button>
        </Box>
      );
    }

    return null;
  }

  return (
    <Box component="section" className="result" sx={{ padding: { xs: "2rem 4%", md: "8rem 10%" }, minHeight: "92vh", textAlign: "center", py: 4 }}>
      <Typography variant="h4" align="center">Prediction Results</Typography>
      <Typography align="center">Results for <b>{pdbName}</b></Typography>
      {jobLink && (
        <Typography align="center" sx={{ mt: 1, fontSize: "0.9rem", wordBreak: "break-all" }}>
          Dedicated result link: <b>{jobLink}</b>
        </Typography>
      )}
      {summary && !showEmptySelectionState && (
        <Typography align="center" sx={{ mt: 1, color: "#4b5563" }}>
          Total matches: <b>{totalMatchCount}</b>
          {summary.top_confidence != null && <> | Top confidence: <b>{Number(summary.top_confidence).toFixed(3)}</b></>}
        </Typography>
      )}
      {usingServerPagination && !showEmptySelectionState && (
        <Typography align="center" sx={{ mt: 1, color: "#6b7280", fontSize: "0.95rem" }}>
          Showing page <b>{(matchPageData?.page || (currentPage + 1))}</b> of <b>{totalPages}</b> from paginated match summaries.
        </Typography>
      )}

      {queryGraphs.length > 1 && (
        <Box sx={{ mt: 3, textAlign: "left" }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Graph Query Interfaces
          </Typography>
          <Select
            multiple
            displayEmpty
            value={selectedQueryGraphs}
            onChange={e => {
              const { value } = e.target;
              setSelectedQueryGraphs(typeof value === "string" ? value.split(",") : value);
            }}
            renderValue={selected => (
              selected.length === 0
                ? "No query graphs selected"
                : selected.length === queryGraphs.length
                  ? `All query graphs (${queryGraphs.length})`
                  : `${selected.length} of ${queryGraphs.length} query graphs`
            )}
            sx={{ minWidth: 340, maxWidth: "100%" }}
          >
            {queryGraphs.map(queryGraph => (
              <MenuItem key={queryGraph.query_pair_id} value={queryGraph.query_pair_id}>
                <Checkbox checked={selectedQueryGraphs.includes(queryGraph.query_pair_id)} />
                <ListItemText
                  primary={`Chain ${(queryGraph.chains || []).join(" ↔ ") || "?"}`}
                  secondary={`${queryGraph.qgraph_size ?? "?"} nodes | ${queryGraph.match_count ?? 0} matches`}
                />
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {chainsWithoutInterface.length > 0 && (
        <Typography align="left" sx={{ mt: 2, color: "#92400e", fontSize: "0.9rem" }}>
          {chainsWithoutInterface.length === 1
            ? `Chain ${chainsWithoutInterface[0]} has no detected interface with another chain.`
            : `Chains ${chainsWithoutInterface.join(", ")} have no detected interface with another chain.`}
        </Typography>
      )}

      {showEmptySelectionState ? (
        <Box sx={{ mt: 4, p: 3, border: "1px dashed #f59e0b", borderRadius: 2, backgroundColor: "#fffbeb", textAlign: "left" }}>
          <Typography sx={{ color: "#92400e", fontWeight: 600 }}>
            Select at least one graph query to view matches.
          </Typography>
        </Box>
      ) : (
      <>
      <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
        <Select value={targetChainFilter} onChange={e => setTargetChainFilter(e.target.value)} displayEmpty>
          <MenuItem value="">All Target Chains</MenuItem>
          {allTargetChains.map(chain => (
            <MenuItem key={chain} value={chain}>{chain}</MenuItem>
          ))}
        </Select>

        <TextField
          label="Graph Query Size"
          type="number"
          value={targetSizeFilter}
          onChange={e => setTargetSizeFilter(e.target.value)}
        />

        <TextField
          label="Graph Pattern Size"
          type="number"
          value={graphPatternSizeFilter}
          onChange={e => setGraphPatternSizeFilter(e.target.value)}
        />

        <TextField
          label="No of Pattern Matches"
          type="number"
          value={numMappingFilter}
          onChange={e => setNumMappingFilter(e.target.value)}
        />

        <TextField
          label="Min Pick Score"
          type="number"
          inputProps={{ step: 0.1 }}
          value={pickScoreFilter}
          onChange={e => setPickScoreFilter(e.target.value)}
        />

        <Select value={sortField} onChange={e => setSortField(e.target.value)} displayEmpty>
          <MenuItem value="">No Sorting</MenuItem>
          <MenuItem value="qgraph_size">Sort by Graph Query Size</MenuItem>
          <MenuItem value="cgraph_size">Sort by Graph Pattern Size</MenuItem>
          <MenuItem value="matched_patterns_total">Sort by No of Pattern Matches</MenuItem>
          <MenuItem value="pick_score">Sort by Pick Score</MenuItem>
        </Select>

        <Select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
          <MenuItem value="asc">Asc</MenuItem>
          <MenuItem value="desc">Desc</MenuItem>
        </Select>
      </Box>

      <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        {targetChainFilter && (
          <Chip label={`Target Chain: ${targetChainFilter}`} onDelete={() => setTargetChainFilter("")} />
        )}
        {targetSizeFilter && (
          <Chip label={`Graph Query Size: ${targetSizeFilter}`} onDelete={() => setTargetSizeFilter("")} />
        )}
        {graphPatternSizeFilter && (
          <Chip label={`Graph Pattern Size: ${graphPatternSizeFilter}`} onDelete={() => setGraphPatternSizeFilter("")} />
        )}
        {numMappingFilter && (
          <Chip label={`No of Pattern Matches: ${numMappingFilter}`} onDelete={() => setNumMappingFilter("")} />
        )}
        {pickScoreFilter && (
          <Chip label={`Min Pick Score: ${pickScoreFilter}`} onDelete={() => setPickScoreFilter("")} />
        )}
        {(targetChainFilter || targetSizeFilter || graphPatternSizeFilter || numMappingFilter || pickScoreFilter) && (
          <Chip
            color="error"
            label="Clear All"
            onClick={() => {
              setTargetChainFilter("");
              setTargetSizeFilter("");
              setGraphPatternSizeFilter("");
              setNumMappingFilter("");
              setPickScoreFilter("");
            }}
          />
        )}
      </Box>

      <Typography sx={{ mt: 2, color: "#6b7280", fontWeight: 500 }}>
        Summary rows load first. Match detail is fetched only when you open a visualization.
      </Typography>
      {matchesLoading && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} />
          <Typography>Loading match page...</Typography>
        </Box>
      )}
      {matchesLoading && !results.length && (
        <Box sx={{ mt: 2 }}>
          {[0, 1, 2, 3, 4].map(index => (
            <Skeleton key={index} variant="rectangular" height={42} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      )}
      {matchesError && !jobError && (
        <Typography sx={{ mt: 2, color: "red", fontWeight: 600 }}>{matchesError}</Typography>
      )}

      <Box sx={{ mt: 2, display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={rollingDownloadIcon(downloading === "json")}
          disabled={Boolean(downloading)}
          onClick={() => handleDownload("json", sortedResults)}
        >
          Download JSON
        </Button>
        <Button
          variant="outlined"
          startIcon={rollingDownloadIcon(downloading === "csv")}
          disabled={Boolean(downloading)}
          onClick={() => handleDownload("csv", sortedResults)}
        >
          Download CSV
        </Button>
      </Box>

      {isTabbed && (
        <Tabs
          value={activeQueryGraphTab}
          onChange={(event, newValue) => setActiveQueryGraphTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mt: 3, borderBottom: "1px solid #e0e0e0" }}
        >
          <Tab
            value={ALL_MATCHES_TAB_ID}
            label={`All Matches (${allMatchesTotal})`}
          />
          {tabbedQueryGraphs.map(queryGraph => (
            <Tab
              key={queryGraph.query_pair_id}
              value={queryGraph.query_pair_id}
              label={`Chain ${(queryGraph.chains || []).join(" ↔ ") || "?"} (${queryGraph.match_count ?? 0})`}
            />
          ))}
        </Tabs>
      )}

      {/* The results table has 8-9 columns of real data -- on a narrow
          screen it needs to scroll horizontally within its own box, not
          drag the entire page (navbar, filters, tabs) into overflow with it. */}
      <Box sx={{ overflowX: "auto" }}>
      <Table sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Graph Query Size</TableCell>
            {queryGraphs.length > 1 && <TableCell>Query Graph</TableCell>}
            <TableCell>Matched PDB</TableCell>
            <TableCell>Graph Pattern Size</TableCell>
            <TableCell>No of Pattern Matches</TableCell>
            <TableCell>Pick Score</TableCell>
            <TableCell>Query-Target Chains</TableCell>
            <TableCell>Pattern-Ligand Chains</TableCell>
            <TableCell>Explore</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedResults.map((row, index) => (
            <TableRow key={row.match_id || row.Match_ID || index}>
              <TableCell>{row.qgraph_size ?? row.Targ_Size}</TableCell>
              {queryGraphs.length > 1 && (
                <TableCell>{(row.qgraph_chains || []).join(" ↔ ") || row.query_pair_id || "—"}</TableCell>
              )}
              <TableCell>{row.cgraph_pdbid ?? row.PDBID_Lig}</TableCell>
              <TableCell>{row.cgraph_size ?? row.CGraph_Size ?? row.Lig_Size}</TableCell>
              <TableCell>{row.matched_patterns_total ?? row.Matched_Patterns_Total}</TableCell>
              <TableCell>{Number(row.pick_score ?? 0).toFixed(3)}</TableCell>
              <TableCell>{getTargetChains(row, targetChainFilter).join(", ")}</TableCell>
              <TableCell>{getLigandChains(row, targetChainFilter).join(", ")}</TableCell>
                <TableCell>
                  <Tooltip
                    title={!targetChainFilter ? "Select a target chain above to enable Explore" : ""}
                    disableHoverListener={Boolean(targetChainFilter)}
                  >
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!currentJobId || !targetChainFilter || detailLoadingMatchId === (row.match_id || row.Match_ID)}
                        onClick={() => handleVisualize(row)}
                      >
                        {detailLoadingMatchId === (row.match_id || row.Match_ID) ? "Loading..." : "Explore"}
                      </Button>
                    </Box>
                  </Tooltip>
                </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </Box>

      <TablePagination
        component="div"
        count={usingServerPagination ? totalMatchCount : sortedResults.length}
        page={currentPage}
        onPageChange={(event, newPage) => setCurrentPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
          setTabPages({});
        }}
        rowsPerPageOptions={[10, 20, 50]}
      />
      </>
      )}
    </Box>
  );
}
